<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';

// Interface for roster selection (different from TournamentRoster)
interface RosterForSelection {
  id: number;
  name: string;
  faction_name: string;
  points_overall?: number;
  points_left?: number;
  overmaxed?: boolean;
}

interface Props {
  eventId: number;
  rosterFormatId: number;
  rosterFormatPoints: number;
  isVisible: boolean;
  requiredRostersCount?: number; // new prop
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
  join: [rosterId: number[]];
}>();

const { locale, t } = useI18n();
const rosters = ref<RosterForSelection[]>([]);
const selectedRosterIds = ref<number[]>([]);
const isLoading = ref(false);
const error = ref<string | null>(null);

const loadRosters = async () => {
  try {
    isLoading.value = true;
    error.value = null;

    const response = await axios.get(`/api/v1/events/user_rosters?roster_format_id=${props.rosterFormatId}`);
    rosters.value = response.data;
  } catch (err: any) {
    console.error('Error loading rosters:', err);
    error.value = t('roster_selection.error_loading_rosters');
  } finally {
    isLoading.value = false;
  }
};

const handleJoin = () => {
  if (selectedRosterIds.value.length < (props.requiredRostersCount || 1)) {
    error.value = t('roster_selection.select_rosters_for_participation', { count: props.requiredRostersCount || 1 });
    return;
  }
  emit('join', selectedRosterIds.value);
};

const handleClose = () => {
  selectedRosterIds.value = [];
  error.value = null;
  emit('close');
};

const hasRosters = computed(() => rosters.value.length > 0);

// Check if user has enough rosters for the tournament
const hasEnoughRosters = computed(() => rosters.value.length >= (props.requiredRostersCount || 1));

// Check if user has some rosters but not enough
const needsMoreRosters = computed(() => hasRosters.value && !hasEnoughRosters.value);

// Check if all rosters are overmaxed
const allRostersOvermaxed = computed(() => {
  return hasRosters.value && rosters.value.every(roster => roster.overmaxed);
});

// Check if user has valid (non-overmaxed) rosters
const hasValidRosters = computed(() => {
  return hasRosters.value && rosters.value.some(roster => !roster.overmaxed);
});

onMounted(() => {
  if (props.isVisible) {
    loadRosters();
  }
});

// Watch for changes to isVisible prop
watch(() => props.isVisible, (newValue) => {
  if (newValue) {
    loadRosters();
  }
});

function toggleRosterSelection(rosterId: number) {
  const roster = rosters.value.find(r => r.id === rosterId);
  if (!roster || roster.overmaxed) {
    return; // Don't allow selection of overmaxed rosters
  }

  const idx = selectedRosterIds.value.indexOf(rosterId);
  if (idx === -1) {
    if (selectedRosterIds.value.length < (props.requiredRostersCount || 1)) {
      selectedRosterIds.value.push(rosterId);
    }
  } else {
    selectedRosterIds.value.splice(idx, 1);
  }
}
</script>

<template>
  <div v-if="isVisible" class="modal-overlay" @click="handleClose">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3>{{ t('roster_selection.select_rosters_title') }}</h3>
        <button class="close-button" @click="handleClose">×</button>
      </div>
      <div class="modal-body">
        <div v-if="error" class="error-message">
          {{ error }}
        </div>
        <div v-if="isLoading" class="loading">
          {{ t('roster_selection.loading_rosters') }}
        </div>
        <div v-else-if="!hasRosters" class="no-rosters">
          <p>{{ t('roster_selection.no_rosters_for_format') }}</p>
          <p>
            <a :href="`/${locale}/rosters/new`" class="create-roster-link">
              {{ t('roster_selection.create_new_roster') }}
            </a>
          </p>
        </div>
        <div v-else-if="allRostersOvermaxed" class="all-rosters-overmaxed">
          <div class="error-message">
            <i class="bi bi-exclamation-triangle"></i>
            <p><strong>{{ t('roster_selection.warning') }}</strong> {{ t('roster_selection.all_rosters_overmaxed_message') }}</p>
            <p>{{ t('roster_selection.need_valid_roster_for_tournament', { points: props.rosterFormatPoints }) }}</p>
          </div>
          <div class="create-valid-roster">
            <p>
              <a :href="`/${locale}/rosters/new`" class="create-roster-link">
                <i class="bi bi-plus-circle"></i>
                {{ t('roster_selection.create_valid_roster') }}
              </a>
            </p>
          </div>
        </div>
        <div v-else-if="needsMoreRosters" class="needs-more-rosters">
          <div class="warning-message">
            <i class="bi bi-exclamation-triangle"></i>
            <p><strong>{{ t('roster_selection.warning') }}</strong> {{ t('roster_selection.need_more_rosters_message', { count: props.requiredRostersCount, current: rosters.length }) }}</p>
            <p>{{ t('roster_selection.recommend_backup_roster') }}</p>
          </div>
          <div class="current-rosters">
            <h4>{{ t('roster_selection.your_current_rosters') }}</h4>
            <div class="roster-options">
              <div
                v-for="roster in rosters"
                :key="roster.id"
                class="roster-option"
                :class="{
                  selected: selectedRosterIds.includes(roster.id),
                  overmaxed: roster.overmaxed
                }"
                @click="() => toggleRosterSelection(roster.id)"
              >
                <div class="roster-info">
                  <div class="roster-name">
                    {{ roster.name }}
                    <span v-if="roster.overmaxed" class="overmaxed-badge">{{ t('roster_selection.overmaxed_limit') }}</span>
                  </div>
                  <div class="roster-faction">{{ roster.faction_name }}</div>
                  <div class="roster-points" :class="{ 'overmaxed-points': roster.overmaxed }">
                    {{ roster.points_overall || 0 }} / {{ props.rosterFormatPoints }} {{ t('events.points') }}
                    <span v-if="roster.overmaxed" class="overmaxed-indicator">(+{{ (roster.points_overall || 0) - props.rosterFormatPoints }})</span>
                  </div>
                </div>
                <div class="roster-selector" @click.stop="() => toggleRosterSelection(roster.id)">
                  <input
                    type="checkbox"
                    :checked="selectedRosterIds.includes(roster.id)"
                    :id="`roster-${roster.id}`"
                    :disabled="roster.overmaxed || (selectedRosterIds.length >= (props.requiredRostersCount || 1) && !selectedRosterIds.includes(roster.id))"
                    @click.stop.prevent="() => toggleRosterSelection(roster.id)"
                    @change.stop
                  />
                  <label
                    :for="`roster-${roster.id}`"
                    @click.stop.prevent="() => toggleRosterSelection(roster.id)"
                  ></label>
                </div>
              </div>
            </div>
          </div>
          <div class="create-backup-roster">
            <p>
              <a :href="`/${locale}/rosters/new`" class="create-roster-link">
                <i class="bi bi-plus-circle"></i>
                {{ t('roster_selection.create_backup_roster') }}
              </a>
            </p>
          </div>
        </div>
        <div v-else class="rosters-list">
          <p class="instruction">
            {{ t('roster_selection.select_rosters_instruction', { count: props.requiredRostersCount || 1 }) }}
          </p>
          <div class="roster-options">
            <div
              v-for="roster in rosters"
              :key="roster.id"
              class="roster-option"
              :class="{
                selected: selectedRosterIds.includes(roster.id),
                overmaxed: roster.overmaxed
              }"
              @click="() => toggleRosterSelection(roster.id)"
            >
              <div class="roster-info">
                <div class="roster-name">
                  {{ roster.name }}
                  <span v-if="roster.overmaxed" class="overmaxed-badge">{{ t('roster_selection.overmaxed_limit') }}</span>
                </div>
                <div class="roster-faction">{{ roster.faction_name }}</div>
                <div class="roster-points" :class="{ 'overmaxed-points': roster.overmaxed }">
                  {{ roster.points_overall || 0 }} / {{ props.rosterFormatPoints }} {{ t('events.points') }}
                  <span v-if="roster.overmaxed" class="overmaxed-indicator">(+{{ (roster.points_overall || 0) - props.rosterFormatPoints }})</span>
                </div>
              </div>
              <div class="roster-selector" @click.stop="() => toggleRosterSelection(roster.id)">
                <input
                  type="checkbox"
                  :checked="selectedRosterIds.includes(roster.id)"
                  :id="`roster-${roster.id}`"
                  :disabled="roster.overmaxed || (selectedRosterIds.length >= (props.requiredRostersCount || 1) && !selectedRosterIds.includes(roster.id))"
                  @click.stop.prevent="() => toggleRosterSelection(roster.id)"
                  @change.stop
                />
                <label
                  :for="`roster-${roster.id}`"
                  @click.stop.prevent="() => toggleRosterSelection(roster.id)"
                ></label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="handleClose">
          {{ t('roster_selection.cancel') }}
        </button>
        <button
          v-if="hasRosters"
          class="btn btn-primary"
          :disabled="selectedRosterIds.length < Math.min(rosters.length, props.requiredRostersCount || 1)"
          @click="handleJoin"
        >
          {{ t('events.participate') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: #efd8b0;
  border: 1px solid #bd9c65;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #bd9c65;
  background-color: #c29c58;
  color: #60451f;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: bold;
  }

  .close-button {
    background: none;
    border: none;
    font-size: 24px;
    color: #60451f;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;

    &:hover {
      background-color: rgba(96, 69, 31, 0.1);
    }
  }
}

.modal-body {
  padding: 20px;
}

.error-message {
  background-color: #ffebee;
  color: #c62828;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 15px;
  border: 1px solid #ffcdd2;
}

.loading,
.no-rosters {
  text-align: center;
  color: #bd9c65;
  padding: 20px;
}

.all-rosters-overmaxed {
  .error-message {
    background-color: #ffebee;
    border: 1px solid #ffcdd2;
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 20px;
    color: #c62828;
    display: flex;
    align-items: flex-start;
    gap: 10px;

    i {
      font-size: 20px;
      color: #f44336;
      flex-shrink: 0;
      margin-top: 2px;
    }

    p {
      margin: 0 0 8px 0;
      line-height: 1.4;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .create-valid-roster {
    text-align: center;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #dee2e6;

    p {
      margin: 0;
    }

    .create-roster-link {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #c29c58;
      text-decoration: none;
      font-weight: bold;
      padding: 10px 20px;
      border: 2px solid #c29c58;
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        background-color: #c29c58;
        color: white;
        text-decoration: none;
      }

      i {
        font-size: 16px;
      }
    }
  }
}

.needs-more-rosters {
  .warning-message {
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 20px;
    color: #856404;
    display: flex;
    align-items: flex-start;
    gap: 10px;

    i {
      font-size: 20px;
      color: #f39c12;
      flex-shrink: 0;
      margin-top: 2px;
    }

    p {
      margin: 0 0 8px 0;
      line-height: 1.4;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .current-rosters {
    margin-bottom: 20px;

    h4 {
      color: #60451f;
      margin-bottom: 15px;
      font-size: 16px;
      font-weight: bold;
    }
  }

  .create-backup-roster {
    text-align: center;
    padding: 15px;
    background-color: #f8f9fa;
    border-radius: 6px;
    border: 1px solid #dee2e6;

    p {
      margin: 0;
    }

    .create-roster-link {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      color: #c29c58;
      text-decoration: none;
      font-weight: bold;
      padding: 10px 20px;
      border: 2px solid #c29c58;
      border-radius: 6px;
      transition: all 0.2s ease;

      &:hover {
        background-color: #c29c58;
        color: white;
        text-decoration: none;
      }

      i {
        font-size: 16px;
      }
    }
  }
}

.create-roster-link {
  color: #c29c58;
  text-decoration: underline;
  font-weight: bold;

  &:hover {
    color: #60451f;
  }
}

.instruction {
  margin-bottom: 15px;
  color: #60451f;
  font-weight: bold;
}

.rosters-list {
  .roster-options {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .roster-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border: 1px solid #bd9c65;
    border-radius: 6px;
    background-color: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #c29c58;
      background-color: #fafafa;
    }

    &.selected {
      border-color: #4caf50;
      background-color: #e8f5e8;
    }

    &.overmaxed {
      border-color: #f44336;
      background-color: #ffebee;
      opacity: 0.7;
      cursor: not-allowed;

      .roster-info {
        .roster-name {
          color: #c62828;
        }

        .roster-points {
          color: #c62828;
        }
      }
    }

    .roster-info {
      flex: 1;

      .roster-name {
        font-weight: bold;
        color: #60451f;
        font-size: 16px;
        margin-bottom: 4px;
      }

      .roster-faction {
        color: #bd9c65;
        font-size: 14px;
        margin-bottom: 2px;
      }

      .roster-points {
        color: #666;
        font-size: 12px;

        &.overmaxed-points {
          color: #c62828;
          font-weight: bold;
        }

        .overmaxed-indicator {
          color: #c62828;
          font-weight: bold;
          margin-left: 4px;
        }
      }

      .overmaxed-badge {
        background-color: #f44336;
        color: white;
        font-size: 10px;
        padding: 2px 6px;
        border-radius: 10px;
        margin-left: 8px;
        font-weight: bold;
      }
    }

    .roster-selector {
      cursor: pointer;
      position: relative;

      input[type="checkbox"] {
        display: none;
      }

      label {
        width: 20px;
        height: 20px;
        border: 2px solid #bd9c65;
        border-radius: 4px; /* Changed from 50% to 4px for better fit */
        display: block;
        position: relative;
        cursor: pointer;
        pointer-events: auto;

        &:after {
          content: '';
          width: 10px;
          height: 10px;
          background-color: #4caf50;
          border-radius: 4px; /* Changed from 50% to 4px for better fit */
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          opacity: 0;
          transition: opacity 0.2s ease;
        }
      }

      input[type="checkbox"]:checked + label {
        border-color: #4caf50;

        &:after {
          opacity: 1;
        }
      }

      input[type="checkbox"]:disabled + label {
        border-color: #e0e0e0;
        background-color: #f0f0f0;
        cursor: not-allowed;
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #bd9c65;
  background-color: #f5f5f5;

  .btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &.btn-primary {
      background-color: #4caf50;
      color: white;

      &:hover:not(:disabled) {
        background-color: #45a049;
      }
    }

    &.btn-secondary {
      background-color: #6c757d;
      color: white;

      &:hover {
        background-color: #5a6268;
      }
    }
  }
}
</style>
